import { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import * as orgService from '../services/organization'
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus'
import { getDemoTherapies, isDemoOrganization } from '../services/demoData'
import { hasReachedDemoLimit, saveDemoEntry, initDemoSession, getDemoEntries } from '../services/demoStorage'
import DemoUpgradePrompt from '../components/DemoUpgradePrompt'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import Textarea from '../components/Textarea'
import Select from '../components/Select'
import SearchBar from '../components/SearchBar'
import BulkActions from '../components/BulkActions'
import Pagination from '../components/Pagination'
import { Calendar, Plus, Clock, User, Activity, X, Trash2, Download, Pencil, Grid, List, CheckCircle2, BarChart3 } from 'lucide-react'
import TherapyCalendar from '../components/TherapyCalendar'
import TherapyCompletionModal from '../components/TherapyCompletionModal'
import TherapyEffectiveness from '../components/TherapyEffectiveness'
import { formatDate } from '../utils/helpers'
import { exportTherapiesToPDF } from '../utils/pdfExport'
import { useFormPersistence } from '../hooks/useFormPersistence'

const ITEMS_PER_PAGE = 12

const therapySchema = z.object({
  clientId: z.string().uuid().optional().nullable(),
  carePlanId: z.string().optional(),
  name: z.string().min(1, 'Therapy name is required'),
  description: z.string().optional(),
  scheduledDate: z.string().min(1, 'Scheduled date is required'),
  timeOfDay: z.string().min(1, 'Time of day is required'),
  frequency: z.enum(['One-time', 'Daily', 'Weekly', 'Monthly']),
  status: z.enum(['scheduled', 'completed', 'missed']),
  assignedUserId: z.string().uuid().optional().nullable(),
})

// These will be translated in the component
const timeOfDayOptions = ['Morning', 'Afternoon', 'Evening']
const frequencyOptions = ['One-time', 'Daily', 'Weekly', 'Monthly']
const statusOptions = ['scheduled', 'completed', 'missed']

const Therapies = () => {
  const { t } = useTranslation()
  const [therapies, setTherapies] = useState([])
  const [clients, setClients] = useState([])
  const [carePlans, setCarePlans] = useState([])
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [editingTherapy, setEditingTherapy] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selected, setSelected] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [viewMode, setViewMode] = useState('list') // 'list' or 'calendar'
  const [selectedTherapy, setSelectedTherapy] = useState(null)
  const [showCompletionModal, setShowCompletionModal] = useState(false)
  const [showEffectiveness, setShowEffectiveness] = useState(false)
  const [effectivenessTherapyId, setEffectivenessTherapyId] = useState(null)
  const [reachedDemoLimit, setReachedDemoLimit] = useState(false)
  const organizationId = useOrganizationId()
  const { canWrite, organization, isAdmin } = useOrganization()
  const { isDemo } = useSubscriptionStatus()

  // Check demo limit on mount
  useEffect(() => {
    if (isDemo) {
      initDemoSession()
      if (hasReachedDemoLimit('therapy')) {
        setReachedDemoLimit(true)
      }
    }
  }, [isDemo])

  const formMethods = useForm({
    resolver: zodResolver(therapySchema),
    defaultValues: {
      clientId: '',
      carePlanId: '',
      name: '',
      description: '',
      scheduledDate: '',
      timeOfDay: 'Morning',
      frequency: 'One-time',
      status: 'scheduled',
    },
  })

  // Form data persistence
  const { dataRestored, clearSavedData } = useFormPersistence(
    formMethods,
    'therapies_form_data',
    null,
    [] // No fields to exclude
  )

  useEffect(() => {
    if (organizationId) {
      fetchTherapies()
      fetchClients()
      fetchCarePlans()
      if (isAdmin) {
        fetchMembers()
      }
    }
  }, [organizationId, isAdmin])

  const fetchTherapies = async () => {
    if (!organizationId) return
    setLoading(true)
    try {
      // For demo organizations, use demo data
      if (isDemo && isDemoOrganization(organization)) {
        const demoTherapies = getDemoTherapies()
        // Merge with any localStorage entries
        const localEntries = getDemoEntries('therapy')
        const allTherapies = [...demoTherapies, ...localEntries]
        setTherapies(allTherapies)
        setLoading(false)
        return
      }

      const { data, error } = await supabase
        .from('therapies')
        .select('*, clients(first_name, last_name), care_plans(title)')
        .eq('organization_id', organizationId)
        .order('scheduled_date', { ascending: true })

      if (error) throw error
      setTherapies(data || [])
    } catch (error) {
      console.error('Error fetching therapies:', error)
      alert(t('therapies.loadError'))
    } finally {
      setLoading(false)
    }
  }

  const fetchClients = async () => {
    if (!organizationId) return
    const { data, error } = await supabase
      .from('clients')
      .select('id, first_name, last_name')
      .eq('organization_id', organizationId)
      .order('first_name')
    if (!error && data) setClients(data)
  }

  const fetchCarePlans = async () => {
    if (!organizationId) return
    const { data, error } = await supabase
      .from('care_plans')
      .select('id, title')
      .eq('organization_id', organizationId)
      .order('title')
    if (!error && data) setCarePlans(data)
  }

  const fetchMembers = async () => {
    if (!organizationId) return
    try {
      const data = await orgService.getOrganizationMembers(organizationId)
      setMembers(data || [])
    } catch (error) {
      console.error('Error fetching members:', error)
    }
  }

  const filteredTherapies = useMemo(() => {
    if (!searchTerm) return therapies
    const term = searchTerm.toLowerCase()
    return therapies.filter(
      (therapy) =>
        therapy.name?.toLowerCase().includes(term) ||
        therapy.description?.toLowerCase().includes(term) ||
        `${therapy.clients?.first_name} ${therapy.clients?.last_name}`.toLowerCase().includes(term) ||
        therapy.status?.toLowerCase().includes(term) ||
        therapy.time_of_day?.toLowerCase().includes(term) ||
        therapy.frequency?.toLowerCase().includes(term)
    )
  }, [therapies, searchTerm])

  const paginatedTherapies = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredTherapies.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredTherapies, currentPage])

  const totalPages = Math.ceil(filteredTherapies.length / ITEMS_PER_PAGE)

  const handleDelete = async (ids) => {
    if (!canWrite('therapies')) {
      alert(t('therapies.permissionDenied'))
      return
    }
    try {
      const { error } = await supabase
        .from('therapies')
        .delete()
        .in('id', ids)
        .eq('organization_id', organizationId)

      if (error) throw error
      setSelected([])
      fetchTherapies()
    } catch (error) {
      console.error('Error deleting therapies:', error)
      alert(t('therapies.deleteError'))
    }
  }

  const handleExport = (ids = null) => {
    exportTherapiesToPDF(therapies, ids)
  }

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]))
  }

  const toggleSelectAll = () => {
    if (selected.length === paginatedTherapies.length) {
      setSelected([])
    } else {
      setSelected(paginatedTherapies.map((t) => t.id))
    }
  }

  const openCreateForm = () => {
    // Check demo limit before opening form
    if (isDemo && hasReachedDemoLimit('therapy')) {
      setReachedDemoLimit(true)
      return
    }
    
    setEditingTherapy(null)
    formMethods.reset({
      clientId: '',
      carePlanId: '',
      name: '',
      description: '',
      scheduledDate: '',
      timeOfDay: 'Morning',
      frequency: 'One-time',
      status: 'scheduled',
      assignedUserId: null,
    })
    setFormOpen(true)
  }

  const openEditForm = (therapy) => {
    setEditingTherapy(therapy)
    formMethods.reset({
      clientId: therapy.client_id,
      carePlanId: therapy.care_plan_id || '',
      name: therapy.name,
      description: therapy.description || '',
      scheduledDate: therapy.scheduled_date,
      timeOfDay: therapy.time_of_day || 'Morning',
      frequency: therapy.frequency || 'One-time',
      status: therapy.status || 'scheduled',
      assignedUserId: therapy.assigned_user_id || null,
    })
    setFormOpen(true)
  }

  const closeForm = () => {
    setFormOpen(false)
    setEditingTherapy(null)
  }

  const onSubmit = async (values) => {
    if (!organizationId) return
    try {
      // Check demo limit
      if (isDemo && !editingTherapy && hasReachedDemoLimit('therapy')) {
        setReachedDemoLimit(true)
        setFormOpen(false)
        return
      }

      // For demo users, save to localStorage instead of database
      if (isDemo) {
        const demoEntry = {
          client_id: values.clientId,
          care_plan_id: values.carePlanId || null,
          name: values.name,
          description: values.description || null,
          scheduled_date: values.scheduledDate,
          time_of_day: values.timeOfDay,
          frequency: values.frequency,
          status: values.status,
        }
        
        saveDemoEntry('therapy', demoEntry)
        clearSavedData()
        formMethods.reset()
        closeForm()
        fetchTherapies()
        return
      }

      const payload = {
        organization_id: organizationId,
        client_id: values.clientId || null,
        care_plan_id: values.carePlanId || null,
        name: values.name,
        description: values.description || null,
        scheduled_date: values.scheduledDate,
        time_of_day: values.timeOfDay,
        frequency: values.frequency,
        status: values.status,
        assigned_user_id: values.assignedUserId || null,
      }

      if (editingTherapy) {
        const { error } = await supabase
          .from('therapies')
          .update(payload)
          .eq('id', editingTherapy.id)
          .eq('organization_id', organizationId)
        if (error) throw error
      } else {
        const { error } = await supabase.from('therapies').insert(payload)
        if (error) throw error
      }

      // Clear saved form data on success
      clearSavedData()
      formMethods.reset()
      closeForm()
      fetchTherapies()
    } catch (error) {
      console.error('Error saving therapy:', error)
      alert(error.message || t('therapies.saveError'))
    }
  }

  const clientOptions = useMemo(
    () =>
      clients.map((client) => ({
        value: client.id,
        label: `${client.first_name} ${client.last_name}`,
      })),
    [clients]
  )

  const carePlanOptions = useMemo(
    () => carePlans.map((plan) => ({ value: plan.id, label: plan.title })),
    [carePlans]
  )

  const memberOptions = useMemo(
    () => [
      { value: '', label: t('therapies.form.assignedToPlaceholder') },
      ...members.map((member) => ({
        value: member.user_id,
        label: member.user?.name || member.user?.email || 'Unknown',
      })),
    ],
    [members, t]
  )

  // Translate options
  const translatedTimeOfDayOptions = useMemo(
    () => timeOfDayOptions.map(opt => ({
      value: opt,
      label: t(`therapies.timeOfDay.${opt.toLowerCase()}`) || opt
    })),
    [t]
  )

  const translatedFrequencyOptions = useMemo(
    () => frequencyOptions.map(opt => {
      const key = opt.toLowerCase().replace('-', '')
      return {
        value: opt,
        label: t(`therapies.frequency.${key}`) || opt
      }
    }),
    [t]
  )

  const translatedStatusOptions = useMemo(
    () => statusOptions.map(opt => ({
      value: opt,
      label: t(`therapies.status.${opt}`) || opt
    })),
    [t]
  )

  return (
    <div className="section-container">
      {/* Upgrade Prompt for Demo Users - Show proactively */}
      {isDemo && (
        <DemoUpgradePrompt featureName="therapies" limitReached={reachedDemoLimit} />
      )}
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center shadow-lg">
              <Calendar size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{t('therapies.title')}</h1>
              <p className="text-gray-600 dark:text-gray-300">
                {t('therapies.subtitle', { count: therapies.length })}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {therapies.length > 0 && (
              <Button variant="secondary" onClick={() => handleExport()} className="flex items-center gap-2">
                <Download size={18} />
                {t('therapies.exportAll')}
              </Button>
            )}
            <Button onClick={openCreateForm} className="flex items-center gap-2 whitespace-nowrap">
              <Plus size={20} />
              {t('therapies.newTherapy')}
            </Button>
          </div>
        </div>
      </motion.div>

      {/* View Toggle & Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between"
      >
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t('therapies.searchPlaceholder')}
          className="flex-1"
        />
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'list' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setViewMode('list')}
            className="flex items-center gap-1"
          >
            <List size={16} />
            List
          </Button>
          <Button
            variant={viewMode === 'calendar' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setViewMode('calendar')}
            className="flex items-center gap-1"
          >
            <Calendar size={16} />
            Calendar
          </Button>
        </div>
      </motion.div>

      {/* Bulk Actions */}
      <BulkActions
        selected={selected}
        onDelete={handleDelete}
        onExport={handleExport}
        onClear={() => setSelected([])}
        resourceName="therapies"
      />

      {loading ? (
        <div className="text-center py-12 text-gray-600 dark:text-gray-300">{t('therapies.form.loading')}</div>
      ) : viewMode === 'calendar' ? (
        <TherapyCalendar
          therapies={filteredTherapies}
          onTherapyUpdate={fetchTherapies}
          onTherapyClick={(therapy) => {
            setSelectedTherapy(therapy)
            setShowEffectiveness(true)
            setEffectivenessTherapyId(therapy.id)
          }}
        />
      ) : filteredTherapies.length === 0 ? (
        <Card variant="soft" className="p-10 text-center">
          <Calendar size={48} className="mx-auto mb-4 text-accent-400" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {searchTerm ? t('therapies.noMatching') : t('therapies.noTherapies')}
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {searchTerm
              ? t('therapies.adjustSearch')
              : t('therapies.getStarted')}
          </p>
          {!searchTerm && (
            <Button onClick={openCreateForm} className="flex items-center gap-2 whitespace-nowrap">
              <Plus size={20} />
              {t('therapies.createFirst')}
            </Button>
          )}
        </Card>
      ) : (
        <>
          {paginatedTherapies.length > 0 && (
            <div className="mb-4 flex items-center gap-2">
              <input
                type="checkbox"
                checked={selected.length === paginatedTherapies.length && paginatedTherapies.length > 0}
                onChange={toggleSelectAll}
                className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-600 dark:text-gray-300">{t('therapies.selectAllPage')}</span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedTherapies.map((therapy, index) => (
              <motion.div
                key={therapy.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card variant="soft" hover className="p-6 h-full flex flex-col relative">
                  {canWrite('therapies') && (
                    <input
                      type="checkbox"
                      checked={selected.includes(therapy.id)}
                      onChange={() => toggleSelect(therapy.id)}
                      className="absolute top-4 right-4 w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 z-10"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <h3 className={`text-lg font-semibold ${therapy.archived_at ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`}>
                        {therapy.name}
                      </h3>
                      {therapy.archived_at && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                          {t('therapies.archived') || 'Archived'}
                        </span>
                      )}
                      {!therapy.client_id && !therapy.archived_at && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                          {t('therapies.general') || 'General'}
                        </span>
                      )}
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-accent-50 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300 border border-accent-200 dark:border-accent-700 capitalize">
                      {therapy.status ? t(`therapies.status.${therapy.status}`) || therapy.status : t('therapies.status.scheduled')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-3">
                    {therapy.description || t('therapies.form.noDescription')}
                  </p>
                  <div className="text-xs text-gray-500 dark:text-gray-400 space-y-2 mb-3">
                    <div className="flex items-center gap-2">
                      <Calendar size={14} />
                      <span>{formatDate(therapy.scheduled_date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={14} />
                      <span>
                        {therapy.time_of_day ? t(`therapies.timeOfDay.${therapy.time_of_day.toLowerCase()}`) || therapy.time_of_day : t('therapies.form.anyTime')} • {therapy.frequency ? t(`therapies.frequency.${therapy.frequency.toLowerCase().replace('-', '')}`) || therapy.frequency : t('therapies.frequency.oneTime')}
                      </span>
                    </div>
                    {therapy.care_plans && (
                      <div className="flex items-center gap-2">
                        <Activity size={14} />
                        <span>{therapy.care_plans.title}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <User size={14} />
                      <span>
                        {therapy.clients
                          ? `${therapy.clients.first_name} ${therapy.clients.last_name}`
                          : therapy.archived_at
                          ? t('therapies.clientArchived') || 'Client archived'
                          : t('therapies.generalTherapy') || 'General Therapy'}
                      </span>
                    </div>
                    {therapy.assigned_user_id && (
                      <div className="flex items-center gap-2">
                        <User size={14} />
                        <span className="text-accent-600 dark:text-accent-400">
                          Assigned: {(() => {
                            const assignedMember = members.find(m => m.user_id === therapy.assigned_user_id)
                            return assignedMember?.user?.name || assignedMember?.user?.email || 'Unknown'
                          })()}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="mt-auto flex gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    {canWrite('therapies') && (
                      <>
                        <button
                          onClick={() => openEditForm(therapy)}
                          className="flex-1 text-center px-3 py-2 text-accent-600 dark:text-accent-400 font-semibold hover:bg-accent-50 dark:hover:bg-accent-900/20 rounded-lg transition-colors text-sm flex items-center justify-center gap-1"
                        >
                          <Pencil size={16} />
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('Delete this therapy? This cannot be undone.')) {
                              handleDelete([therapy.id])
                            }
                          }}
                          className="px-3 py-2 text-red-600 dark:text-red-400 font-semibold hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-sm flex items-center gap-1"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={ITEMS_PER_PAGE}
            totalItems={filteredTherapies.length}
          />
        </>
      )}

      {/* Form Drawer */}
      <AnimatePresence>
        {formOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeForm}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-lg z-50 p-6 overflow-y-auto glass-card"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-sm uppercase text-gray-400 dark:text-gray-500 font-semibold tracking-wide">
                    {editingTherapy ? t('therapies.form.update') : t('therapies.form.schedule')}
                  </p>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {editingTherapy ? t('therapies.form.editTherapy') : t('therapies.form.newTherapy')}
                  </h3>
                </div>
                <button
                  onClick={closeForm}
                  className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X size={20} />
                </button>
              </div>

              <FormProvider {...formMethods}>
                {/* Data Restored Indicator */}
                {dataRestored && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-800 dark:text-green-200"
                  >
                    ✓ {t('therapies.form.dataRestored')}
                  </motion.div>
                )}

                <form className="space-y-4" onSubmit={formMethods.handleSubmit(onSubmit)}>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      {t('therapies.form.client')} <span className="text-gray-500 text-xs">({t('therapies.form.optional') || 'Optional'})</span>
                    </label>
                    <Select
                      options={[
                        { label: t('therapies.form.generalTherapy') || 'General Therapy (No Client)', value: '' },
                        ...clientOptions
                      ]}
                      error={formMethods.formState.errors.clientId?.message}
                      {...formMethods.register('clientId', {
                        onChange: (e) => {
                          const value = e.target.value || null
                          formMethods.setValue('clientId', value)
                        },
                      })}
                    />
                    {!formMethods.watch('clientId') && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {t('therapies.form.generalTherapyDescription') || 'This therapy will be available for all clients'}
                      </p>
                    )}
                  </div>
                  <Select
                    label={t('therapies.form.carePlan')}
                    options={carePlanOptions}
                    placeholder={t('therapies.form.optional')}
                    error={formMethods.formState.errors.carePlanId?.message}
                    {...formMethods.register('carePlanId')}
                  />
                  <Input
                    label={t('therapies.form.therapyName')}
                    placeholder={t('therapies.form.therapyNamePlaceholder')}
                    required
                    error={formMethods.formState.errors.name?.message}
                    {...formMethods.register('name')}
                  />
                  <Textarea
                    label={t('therapies.form.description')}
                    placeholder={t('therapies.form.descriptionPlaceholder')}
                    error={formMethods.formState.errors.description?.message}
                    {...formMethods.register('description')}
                  />
                  <Input
                    type="date"
                    label={t('therapies.form.scheduledDate')}
                    required
                    error={formMethods.formState.errors.scheduledDate?.message}
                    {...formMethods.register('scheduledDate')}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Select
                      label={t('therapies.form.timeOfDay')}
                      options={translatedTimeOfDayOptions}
                      required
                      error={formMethods.formState.errors.timeOfDay?.message}
                      {...formMethods.register('timeOfDay')}
                    />
                    <Select
                      label={t('therapies.form.frequencyRequired') || t('therapies.form.frequency') + '*'}
                      options={translatedFrequencyOptions}
                      required
                      error={formMethods.formState.errors.frequency?.message}
                      {...formMethods.register('frequency')}
                    />
                  </div>
                  <Select
                    label={t('therapies.form.status')}
                    options={translatedStatusOptions}
                    required
                    error={formMethods.formState.errors.status?.message}
                    {...formMethods.register('status')}
                  />
                  {isAdmin && (
                    <Select
                      label={t('therapies.form.assignedTo')}
                      options={memberOptions}
                      placeholder={t('therapies.form.assignedToPlaceholder')}
                      error={formMethods.formState.errors.assignedUserId?.message}
                      {...formMethods.register('assignedUserId')}
                    />
                  )}
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1">
                      {editingTherapy ? t('therapies.form.updateTherapy') : t('therapies.form.scheduleTherapy')}
                    </Button>
                    <Button type="button" variant="secondary" className="flex-1" onClick={closeForm}>
                      {t('therapies.form.cancel')}
                    </Button>
                  </div>
                </form>
              </FormProvider>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Therapies
